import React from "react";
import {
  List,
  TopToolbar,
  useUpdate,
  useDelete,
  useRecordContext,
  useListContext,
  useRefresh,
} from "react-admin";
import { useMediaQuery } from "@mui/material";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Order, OrderItem } from "../types";

const STATUS_LABELS: Record<string, string> = {
  payment_pending: "Bezahlung offen",
  in_progress: "In Bearbeitung",
  completed: "Abgeschlossen",
};

const STATUS_COLORS: Record<string, "warning" | "info" | "success" | "default"> = {
  payment_pending: "warning",
  in_progress: "info",
  completed: "success",
};

function formatPrice(price: number): string {
  return `€ ${price.toFixed(2).replace(".", ",")}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("de-AT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusSelect({ record }: { record: Order }) {
  const [update, { isPending }] = useUpdate();

  const handleChange = (e: SelectChangeEvent) => {
    e.stopPropagation();
    update("shop-orders", {
      id: record.id,
      data: { status: e.target.value },
      previousData: record,
    });
  };

  return (
    <Select
      value={record.status ?? "payment_pending"}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      disabled={isPending}
      size="small"
      variant="outlined"
      sx={{ minWidth: 160, fontSize: "0.8rem" }}
    >
      <MenuItem value="payment_pending">Bezahlung offen</MenuItem>
      <MenuItem value="in_progress">In Bearbeitung</MenuItem>
      <MenuItem value="completed">Abgeschlossen</MenuItem>
    </Select>
  );
}

function PaymentMethodSelect({ record }: { record: Order }) {
  const [update, { isPending }] = useUpdate();

  const handleChange = (e: SelectChangeEvent) => {
    e.stopPropagation();
    update("shop-orders", {
      id: record.id,
      data: { payment_method: e.target.value },
      previousData: record,
    });
  };

  return (
    <Select
      value={record.payment_method ?? "cash"}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      disabled={isPending}
      size="small"
      variant="outlined"
      sx={{ minWidth: 110, fontSize: "0.8rem" }}
    >
      <MenuItem value="cash">Bar</MenuItem>
      <MenuItem value="paypal">PayPal</MenuItem>
    </Select>
  );
}

function DeleteOrderButton({ record }: { record: Order }) {
  const [open, setOpen] = React.useState(false);
  const [deleteOne, { isPending }] = useDelete();
  const refresh = useRefresh();

  const handleDelete = () => {
    deleteOne(
      "shop-orders",
      { id: record.id, previousData: record },
      { onSuccess: () => { setOpen(false); refresh(); } }
    );
  };

  return (
    <>
      <Tooltip title="Bestellung löschen">
        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Bestellung löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bestellung {record.order_number ? `#${record.order_number} ` : ""}von <strong>{record.customer_name}</strong> ({formatDate(record.created_at)}) wirklich löschen?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button color="error" onClick={handleDelete} disabled={isPending}>Löschen</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ItemsList({ items, notes }: { items?: OrderItem[]; notes?: string }) {
  if (!items?.length) return null;
  return (
    <Box>
      {items.map((item, i) => (
        <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", py: 0.5, gap: 1 }}>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {item.shop_item_name} × {item.quantity}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.25}>
              {item.size && <Chip label={`Größe: ${item.size}`} size="small" />}
              {item.imprint && <Chip label={`Aufdruck: ${item.imprint}`} size="small" color="info" />}
            </Stack>
          </Box>
          <Typography variant="body2" fontWeight={500} whiteSpace="nowrap">
            {formatPrice(item.subtotal)}
          </Typography>
        </Box>
      ))}
      {notes && (
        <Typography variant="body2" color="text.secondary" mt={1}>
          Anmerkung: {notes}
        </Typography>
      )}
    </Box>
  );
}

function OrderItemsPanel() {
  const record = useRecordContext<Order>();
  if (!record) return null;
  return (
    <Box p={2} bgcolor="background.default">
      <Typography variant="subtitle2" mb={1} color="text.secondary">
        Bestelldetails
      </Typography>
      <ItemsList items={record.items} notes={record.notes} />
    </Box>
  );
}

function MobileOrderCard({ record }: { record: Order }) {
  const [expanded, setExpanded] = React.useState(false);
  const status = record.status ?? "payment_pending";

  return (
    <Card sx={{ mb: 1, borderLeft: "4px solid", borderLeftColor: `${STATUS_COLORS[status]}.main` }}>
      <CardContent sx={{ pb: "8px !important" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box flex={1} minWidth={0}>
            {record.order_number && (
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                #{record.order_number}
              </Typography>
            )}
            <Typography fontWeight={600}>{record.customer_name}</Typography>
            {record.customer_email && (
              <Typography variant="caption" color="text.secondary" display="block">
                {record.customer_email}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {formatDate(record.created_at)}
            </Typography>
            <Typography variant="subtitle2" color="primary" mt={0.5}>
              {formatPrice(record.total)} · {record.items?.length ?? 0} Artikel
            </Typography>
            <Box mt={1} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <StatusSelect record={record} />
              <PaymentMethodSelect record={record} />
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <DeleteOrderButton record={record} />
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Divider sx={{ mt: 1, mb: 1 }} />
          <ItemsList items={record.items} notes={record.notes} />
        </Collapse>
      </CardContent>
    </Card>
  );
}

function MobileOrderList() {
  const { data, isPending } = useListContext<Order>();
  if (isPending || !data) return null;
  return (
    <Box>
      {data.map((record) => (
        <MobileOrderCard key={record.id} record={record} />
      ))}
    </Box>
  );
}

export function OrderList() {
  const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));

  return (
    <List
      title="Bestellungen"
      sort={{ field: "created_at", order: "DESC" }}
      actions={<TopToolbar />}
    >
      {isSmall ? (
        <MobileOrderList />
      ) : (
        <Box>
          <Box sx={{ display: "flex", px: 2, py: 1, bgcolor: "action.hover", borderRadius: 1, mb: 0.5 }}>
            <Box sx={{ width: 80, mr: 2 }}><Typography variant="caption" color="text.secondary">Best.-Nr.</Typography></Box>
            <Box sx={{ width: 130, mr: 2 }}><Typography variant="caption" color="text.secondary">Datum</Typography></Box>
            <Box flex={1}><Typography variant="caption" color="text.secondary">Name</Typography></Box>
            <Box sx={{ width: 180, mr: 2 }}><Typography variant="caption" color="text.secondary">E-Mail</Typography></Box>
            <Box sx={{ width: 60, mr: 2 }}><Typography variant="caption" color="text.secondary">Artikel</Typography></Box>
            <Box sx={{ width: 80, mr: 2 }}><Typography variant="caption" color="text.secondary">Gesamt</Typography></Box>
            <Box sx={{ width: 180, mr: 2 }}><Typography variant="caption" color="text.secondary">Status</Typography></Box>
            <Box sx={{ width: 110, mr: 2 }}><Typography variant="caption" color="text.secondary">Zahlungsart</Typography></Box>
            <Box sx={{ width: 40 }} />
          </Box>
          <OrderRows />
        </Box>
      )}
    </List>
  );
}

function OrderRows() {
  const { data, isPending } = useListContext<Order>();
  if (isPending || !data) return null;
  return (
    <>
      {data.map((record) => (
        <ExpandableOrderRow key={record.id} record={record} />
      ))}
    </>
  );
}

function ExpandableOrderRow({ record }: { record: Order }) {
  const [expanded, setExpanded] = React.useState(false);
  const status = record.status ?? "payment_pending";

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          borderLeft: "4px solid",
          borderLeftColor: `${STATUS_COLORS[status]}.main`,
          "&:hover": { bgcolor: "action.hover" },
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ width: 80, mr: 2 }}>
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            {record.order_number ? `#${record.order_number}` : "–"}
          </Typography>
        </Box>
        <Box sx={{ width: 130, mr: 2 }}>
          <Typography variant="body2">{formatDate(record.created_at)}</Typography>
        </Box>
        <Box flex={1}>
          <Typography variant="body2" fontWeight={500}>{record.customer_name}</Typography>
        </Box>
        <Box sx={{ width: 180, mr: 2 }}>
          <Typography variant="body2" noWrap>{record.customer_email ?? "–"}</Typography>
        </Box>
        <Box sx={{ width: 60, mr: 2 }}>
          <Typography variant="body2">{record.items?.length ?? 0}×</Typography>
        </Box>
        <Box sx={{ width: 80, mr: 2 }}>
          <Typography variant="body2">{formatPrice(record.total)}</Typography>
        </Box>
        <Box sx={{ width: 180, mr: 2 }} onClick={(e) => e.stopPropagation()}>
          <StatusSelect record={record} />
        </Box>
        <Box sx={{ width: 110, mr: 2 }} onClick={(e) => e.stopPropagation()}>
          <PaymentMethodSelect record={record} />
        </Box>
        <Box sx={{ width: 40, display: "flex", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
          <DeleteOrderButton record={record} />
        </Box>
      </Box>
      {expanded && (
        <Box sx={{ px: 4, py: 1.5, bgcolor: "background.default", borderBottom: "1px solid", borderColor: "divider" }}>
          <ItemsList items={record.items} notes={record.notes} />
        </Box>
      )}
    </>
  );
}
